import React, { useEffect, useState } from "react";
import { useLinkableEmployees } from "../../../api/queries/adminQueries";

/**
 * User form: pick the employee to link, only from the account's company and
 * branch (GET /users/linkable-employees). Server-side search; userId keeps
 * the edited account's own employee in the list.
 * value: { id, name, employeeNumber, branchId, branchName } or null.
 */
const LinkableEmployeePicker = ({
  companyId,
  branchId,
  userId,
  value,
  onChange,
  id,
}) => {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const query = useLinkableEmployees(
    {
      companyId,
      branchId: branchId || undefined,
      userId: userId || undefined,
      search: debounced || undefined,
      limit: 20,
    },
    open && !value,
  );
  const options = Array.isArray(query.data) ? query.data : [];

  if (value) {
    return (
      <div className="employee-picked">
        <div>
          <div className="fw-semibold text-navy">{value.name || "—"}</div>
          <div className="text-slate-500 fs-075">
            {value.employeeNumber}
            {value.branchName ? ` · ${value.branchName}` : ""}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-link text-slate-600"
          onClick={() => onChange(null)}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="employee-picker">
      <div className="position-relative">
        <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
        <input
          type="search"
          className="form-control ps-5"
          id={id}
          placeholder={
            companyId
              ? branchId
                ? "Search an employee of this branch (optional)"
                : "Search an employee of this company (optional)"
              : "Select a company first"
          }
          value={search}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          disabled={!companyId}
          aria-label="Search employee to link"
        />
      </div>
      {open && companyId && (
        <ul className="employee-picker-results" role="listbox">
          {query.isFetching && options.length === 0 && (
            <li className="text-slate-500">Searching…</li>
          )}
          {query.isError && (
            <li className="text-danger">Could not load employees.</li>
          )}
          {!query.isFetching && !query.isError && options.length === 0 && (
            <li className="text-slate-500">
              No free employees in this {branchId ? "branch" : "company"}.
            </li>
          )}
          {options.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                role="option"
                aria-selected="false"
                onClick={() => {
                  onChange(e);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <span className="fw-semibold">{e.name}</span>
                <span className="text-slate-500 fs-075 ms-2">
                  {e.employeeNumber}
                </span>
                {e.branchName && (
                  <span className="text-slate-400 fs-070 ms-auto">
                    {e.branchName}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LinkableEmployeePicker;
